import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareWarning, Plus } from "lucide-react";
import { toast } from "sonner";

const FIELDS = ["Mobile", "Personal Email", "Current Address", "Emergency Contact", "Bank Account", "Name Spelling", "Other"];

export default function RequestCorrection() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ field: "", current_value: "", requested_value: "", reason: "" });

  const { data, isLoading } = useQuery({ queryKey: ["myReq"], queryFn: async () => (await api.get("/my/change-requests")).data });
  const submit = useMutation({
    mutationFn: async () => (await api.post("/my/change-requests", form)).data,
    onSuccess: () => { toast.success("Correction request submitted"); qc.invalidateQueries({ queryKey: ["myReq"] }); setOpen(false); setForm({ field: "", current_value: "", requested_value: "", reason: "" }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader overline="Self-Service" title="Request Correction" icon={MessageSquareWarning}
        subtitle="You cannot edit HR-controlled fields directly. Submit a request and HR will review it."
        actions={<Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => setOpen(true)} data-testid="new-request-btn"><Plus className="h-4 w-4 mr-1" /> New Request</Button>} />

      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <EmptyState icon={MessageSquareWarning} title="No correction requests" hint="Submit a request to have HR update your details."
          action={<Button className="bg-navy" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> New Request</Button>} />
      ) : (
        <div className="space-y-3" data-testid="my-requests">
          {data.map((r) => (
            <Card key={r.id} className="p-4 border-slate-200 shadow-none flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-navy">{r.field}</p>
                <p className="text-sm text-slate-500 mt-0.5">Requested: <span className="text-navy">{r.requested_value}</span></p>
                <p className="text-xs text-slate-400 mt-1">Reason: {r.reason}</p>
                {r.remarks && <p className="text-xs text-slate-500 mt-1">HR remarks: {r.remarks}</p>}
              </div>
              <StatusBadge status={r.status === "Pending" ? "Pending" : r.status === "Approved" ? "Active" : "Non-compliant"} />
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="request-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Request a Correction</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Field *</Label>
              <Select value={form.field} onValueChange={(v) => setForm({ ...form, field: v })}>
                <SelectTrigger className="mt-1" data-testid="req-field"><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent className="bg-white">{FIELDS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Current Value</Label><Input value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="mt-1" /></div>
            <div><Label>Requested Value *</Label><Input value={form.requested_value} onChange={(e) => setForm({ ...form, requested_value: e.target.value })} className="mt-1" data-testid="req-value" /></div>
            <div><Label>Reason *</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" data-testid="req-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!form.field || !form.requested_value || form.reason.length < 3 || submit.isPending} onClick={() => submit.mutate()} data-testid="submit-request">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
